import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
  DataTable,
  Badge,
  Box,
  Modal,
  TextField,
  Select,
  Pagination,
  EmptyState,
} from '@shopify/polaris';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Solved', value: 'solved' },
];

const FILTER_OPTIONS = [
  { label: 'All Statuses', value: '' },
  ...STATUS_OPTIONS,
];

const STATUS_BADGE = {
  pending: { tone: 'warning', label: 'Pending' },
  in_progress: { tone: 'info', label: 'In Progress' },
  solved: { tone: 'success', label: 'Solved' },
};

export default function AdminSupport() {
  const navigate = useNavigate();
  const { isAuthenticated, adminFetch } = useAdminAuth();
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editStatus, setEditStatus] = useState('pending');
  const [editNotes, setEditNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadTickets = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());

      const response = await adminFetch(`/api/admin/tickets?${params}`);
      const data = await response.json();

      if (data.success) {
        setTickets(data.tickets);
        setPagination(data.pagination);
      } else {
        setError(data.error || 'Failed to load tickets');
      }
    } catch {
      setError('Failed to load tickets');
    }
    setIsLoading(false);
  }, [adminFetch, statusFilter, search]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadTickets();
  }, [isAuthenticated, navigate, loadTickets]);

  const handleOpenEdit = useCallback((ticket) => {
    setSelectedTicket(ticket);
    setEditStatus(ticket.status);
    setEditNotes(ticket.admin_notes || '');
    setShowEditModal(true);
  }, []);

  const handleSaveTicket = useCallback(async () => {
    if (!selectedTicket) return;

    setActionLoading(true);
    setError(null);

    try {
      const response = await adminFetch(`/api/admin/tickets/${selectedTicket.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: editStatus, admin_notes: editNotes }),
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Ticket updated successfully');
        setShowEditModal(false);
        loadTickets(pagination.page);
      } else {
        setError(data.error || 'Failed to update ticket');
      }
    } catch {
      setError('Failed to update ticket');
    }

    setActionLoading(false);
  }, [selectedTicket, editStatus, editNotes, adminFetch, loadTickets, pagination.page]);

  if (!isAuthenticated) return null;

  const rows = tickets.map((ticket) => {
    const badge = STATUS_BADGE[ticket.status] || STATUS_BADGE.pending;
    return [
      ticket.id,
      ticket.shop_domain || '-',
      ticket.subject.length > 40 ? ticket.subject.substring(0, 40) + '...' : ticket.subject,
      <Badge tone={badge.tone}>{badge.label}</Badge>,
      <Badge tone={ticket.email_delivered ? 'success' : 'warning'}>
        {ticket.email_delivered ? 'Yes' : 'No'}
      </Badge>,
      new Date(ticket.created_at).toLocaleDateString(),
      <Button variant="plain" onClick={() => handleOpenEdit(ticket)}>View</Button>,
    ];
  });

  return (
    <Page
      title="Support Tickets"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/admin/dashboard') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              <p>{success}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="300" blockAlign="end">
                <div style={{ width: '200px' }}>
                  <Select
                    label="Status"
                    options={FILTER_OPTIONS}
                    value={statusFilter}
                    onChange={(val) => { setStatusFilter(val); }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Search"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search by shop domain..."
                    autoComplete="off"
                  />
                </div>
                <Button onClick={() => loadTickets(1)}>Filter</Button>
              </InlineStack>

              {isLoading ? (
                <InlineStack align="center">
                  <Spinner />
                </InlineStack>
              ) : tickets.length === 0 ? (
                <EmptyState heading="No tickets found" image="">
                  <p>No support tickets match the current filters.</p>
                </EmptyState>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['ID', 'Shop', 'Subject', 'Status', 'Delivered', 'Created', '']}
                    rows={rows}
                  />
                  {pagination.pages > 1 && (
                    <InlineStack align="center">
                      <Pagination
                        hasPrevious={pagination.page > 1}
                        hasNext={pagination.page < pagination.pages}
                        onPrevious={() => loadTickets(pagination.page - 1)}
                        onNext={() => loadTickets(pagination.page + 1)}
                      />
                    </InlineStack>
                  )}
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Ticket Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Ticket #${selectedTicket?.id} - ${selectedTicket?.shop_domain}`}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveTicket,
          loading: actionLoading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowEditModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text variant="headingSm">Subject</Text>
              <Text>{selectedTicket?.subject}</Text>
            </BlockStack>

            <BlockStack gap="200">
              <Text variant="headingSm">Message</Text>
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <Text>{selectedTicket?.message}</Text>
              </Box>
            </BlockStack>

            <InlineStack gap="300">
              <Text variant="bodySm" tone="subdued">
                From: {selectedTicket?.shop_email || 'N/A'}
              </Text>
              <Text variant="bodySm" tone="subdued">
                Submitted: {selectedTicket?.created_at ? new Date(selectedTicket.created_at).toLocaleString() : '-'}
              </Text>
            </InlineStack>

            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={editStatus}
              onChange={setEditStatus}
            />

            <TextField
              label="Admin Notes"
              value={editNotes}
              onChange={setEditNotes}
              multiline={4}
              placeholder="Add internal notes or a reply..."
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
